import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import queryString from "query-string";
import moment from "moment";
import Filters from "./Filters";
import ReportService from "../../../services/reportService";

import { formatDate } from "../../../helpers/date";

import ProjectSummaryList from "./ProjectSummaryList";
import { downloadCSV } from "../utils";
import { saveFilterState, cleanHTMLString } from "../../../utils";
import { CONTRACTS_BY_TYPE_COLUMNS } from "./columns";

import * as codeActions from "../../../actions/codeActions";
import * as exportActions from "../../../actions/exportActions";

const ProjectSummaryPage = ({
  history,
  codes,
  actions,
  exported,
  exporting
}) => {
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState(0);
  const [projects, setProjects] = useState([]);
  const [sorted, setSorted] = useState([{ id: "createdAt", desc: false }]);

  const [filters, setFilters] = useState({
    page: 0,
    pageSize: 10
  });

  const [data, setData] = useState([]);

  useEffect(() => {
    if (!codes.length) {
      actions.loadCodes();
    }
  }, [actions, codes]);

  useEffect(() => {
    if (exported) {
      const url = window.URL.createObjectURL(
        new Blob([exported], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Project-Summary-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      actions.clearExported();
    }
  }, [exported, actions]);

  useEffect(() => {
    if (codes?.length) {
      const projectCategory = codes.filter(
        (code) =>
          code.entity === "Project" &&
          code.field === "project_category" &&
          code.order === 2
      );
      const inProcessStatus = codes.filter(
        (code) =>
          code.entity === "Project" &&
          code.field === "status" &&
          code.order === 1
      );
      setFilters((prev) => ({
        ...prev,
        category: projectCategory.map((option) => option._id),
        project_status: inProcessStatus.map((option) => option._id)
      }));
    }
  }, [codes]);

  const fetchData = useCallback(
    async (pageNumber = filters.page, size = filters.pageSize, rtSorted) => {
      setLoading(true);
      const newSorted = rtSorted || sorted;
      let query = {
        groupByField: "customer",
        ...newSorted.reduce((acc, curr) => {
          acc[`sort.${curr.id}`] = curr.desc ? 1 : -1;
          return acc;
        }, {})
      };
      if (filters.resource1 && filters.resource1.length) {
        query.resource1 = filters.resource1;
      }
      if (filters.customers && filters.customers.length) {
        query.customers = filters.customers;
      }
      if (filters.projects && filters.projects.length) {
        query.projects = filters.projects;
      }
      if (filters.departments && filters.departments.length) {
        query.departments = filters.departments;
      }
      if (filters.call_type && filters.call_type.length) {
        query.call_type = filters.call_type;
      }
      if (filters.resource2 && filters.resource2.length) {
        query.resource2 = filters.resource2;
      }
      if (filters.project_status && filters.project_status.length) {
        query.project_status = filters.project_status;
      }
      if (filters.project_manager && filters.project_manager.length) {
        query.project_manager = filters.project_manager;
      }
      if (filters.category && filters.category.length) {
        query.category = filters.category;
      }
      if (filters.pageSize || size) {
        query.pageSize = size || filters.pageSize;
      }
      query.page = pageNumber;
      query = queryString.stringify(query, { arrayFormat: "bracket" });
      const { data: result, pages: numPages } = await ReportService.get(
        `/project-summary?${query}`
      );
      setData(result);
      setPages(numPages);
      setLoading(false);
    },
    [filters, setData, setLoading, sorted]
  );

  const handleChange = (name) => (value) => {
    setLoading(false);
    if (Array.isArray(value)) {
      saveFilterState("PROJECT_SUMMARY_REPORT", {
        ...filters,
        [name]: value.map((v) => v.value)
      });
      setFilters({ ...filters, [name]: value.map((v) => v.value) });
    } else {
      saveFilterState("PROJECT_SUMMARY_REPORT", { ...filters, [name]: value });
      setFilters({ ...filters, [name]: value });
    }
  };

  const handleRowClick = (rowInfo) => {
    history.push(`/app/projects/${rowInfo.original._id}`);
  };

  const handleSortedChange = async (rtSorted) => {
    setSorted(rtSorted);
    fetchData(null, null, rtSorted);
  };

  const handlePageSizeChange = async (pageSize) => {
    saveFilterState("PROJECT_SUMMARY_REPORT", { ...filters, pageSize });
    setFilters({ ...filters, pageSize });
    fetchData(null, pageSize);
  };

  const handlePageChange = async (page) => {
    saveFilterState("PROJECT_SUMMARY_REPORT", { ...filters, page });
    setFilters({ ...filters, page });
    fetchData(page, null);
  };

  const handleExport = async () => {
    setLoading(true);
    const newSorted = sorted;
    let query = {
      groupByField: "customer",
      ...newSorted.reduce((acc, curr) => {
        acc[`sort.${curr.id}`] = curr.desc ? 1 : -1;
        return acc;
      }, {})
    };

    if (filters.resource1 && filters.resource1.length) {
      query.resource1 = filters.resource1;
    }
    if (filters.customers && filters.customers.length) {
      query.customers = filters.customers;
    }
    if (filters.projects && filters.projects.length) {
      query.projects = filters.projects;
    }
    if (filters.departments && filters.departments.length) {
      query.departments = filters.departments;
    }
    if (filters.call_type && filters.call_type.length) {
      query.call_type = filters.call_type;
    }
    if (filters.resource2 && filters.resource2.length) {
      query.resource2 = filters.resource2;
    }
    if (filters.project_status && filters.project_status.length) {
      query.project_status = filters.project_status;
    }
    if (filters.project_manager && filters.project_manager.length) {
      query.project_manager = filters.project_manager;
    }
    if (filters.category && filters.category.length) {
      query.category = filters.category;
    }
    query.pageSize = null;
    query.page = null;
    query = queryString.stringify(query, { arrayFormat: "bracket" });
    const { data: result } = await ReportService.get(
      `/project-summary?${query}`
    );

    const columns = CONTRACTS_BY_TYPE_COLUMNS.map((column) => {
      if (column.id === "notes") {
        return {
          Header: column.Header,
          accessor: column.accessor
        };
      }
      return column;
    });

    const title = `Project Summary By Project Manager As Of ${moment().format(
      "MM/DD/YYYY"
    )}`;

    downloadCSV(
      `ProjectSummary-${formatDate(moment(), "yyyyMMdd")}`,
      result.map((item) => ({
        ...item,
        notes: item.notes?.note
          ? `${item.notes.date} - ${cleanHTMLString(item.notes.note, true)}`
          : ""
      })),
      columns,
      title
    );
    setLoading(false);
  };

  const handleResetFilters = () => {
    const projectCategory = codes.filter(
      (code) =>
        code.entity === "Project" &&
        code.field === "project_category" &&
        code.order === 2
    );
    const inProcessStatus = codes.filter(
      (code) =>
        code.entity === "Project" && code.field === "status" && code.order === 1
    );
    setFilters({
      page: 0,
      pageSize: 10,
      category: projectCategory.map((option) => option._id),
      project_status: inProcessStatus.map((option) => option._id)
    });
  };

  const handlePDFExport = () => {
    const pdfFilters = filters;

    delete pdfFilters.page;
    delete pdfFilters.pageSize;

    const payload = {
      data: { ...pdfFilters, "sort.createdAt": 1 },
      type: "PROJECT_SUMMARY_BY_PM",
      format: "pdf"
    };
    actions.performExport(payload);
  };

  return (
    <div className="report-page">
      <Filters
        filters={filters}
        onChange={handleChange}
        codes={codes}
        setProjects={setProjects}
        projects={projects}
        fetchData={() => fetchData()}
        onExportClick={handleExport}
        onPDFExportClick={handlePDFExport}
        handleResetFilters={handleResetFilters}
        exportDisabled={loading || exporting || !data?.length}
        loading={loading}
      />
      <ProjectSummaryList
        data={data}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSortedChange={handleSortedChange}
        page={filters.page}
        pages={pages}
        sorted={sorted}
        pageSize={filters.pageSize}
        handleExport={handleExport}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

ProjectSummaryPage.propTypes = {
  history: PropTypes.object,
  codes: PropTypes.array,
  actions: PropTypes.object,
  exported: PropTypes.string,
  exporting: PropTypes.bool
};

const mapStatesToProps = (state) => ({
  codes: state.reducers.codes,
  exporting: state.reducers.exporting,
  exported: state.reducers.exported
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...codeActions,
      ...exportActions
    },
    dispatch
  )
});

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(ProjectSummaryPage);
